import { useSelector } from 'react-redux';
import selectors from '../../../../redux/selectors/selectors';
import workflowScreenSelectors from '../../redux/selectors/workflowScreenSelectors';
import { FiStar, MdInput } from '../../../../components/react-icons/main';
import { flattenGraphSequence } from '@shared/graph/flattenGraphSequence.js';
import fuzzyMatch from './fuzzyMatch';
import getAttributes from './lib/getAttributes';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import useWorkflowMemory from '@/screens/workflows/hooks/useWorkflowMemory.js';
import { formatNestedAttributeKey } from '@shared/workflow/formatNestedAttributeKey.js';

const getParentSteps = (workflow, apps, stepId) => {
  const graph = workflow?.graph;
  const steps = workflow?.steps;
  const sequence = flattenGraphSequence(graph, steps, null, true).filter((n) => n?.type !== 'folder');
  const index = sequence.findIndex((n) => n.id === stepId);
  const result = sequence
    .slice(0, index)
    .map((n) => ({ id: n?.id, step: steps?.find((d) => d?.id === n?.id) }))
    .map((n) => ({ ...n, app: apps?.find((d) => d?.id === n?.step?.appId) }));
  return result.filter((n) => !['appSelector'].includes(n.app?.slug));
};

const getArrayMapSteps = (workflow, argumentValues, argKey) => {
  // Find which arguments have been saved as arrayMaps, then return a modified step with the arrayMapKey and isArrayMap flag. These are used for setting and retrieving in Variable Pane.
  if (!argKey) {
    return [];
  }
  const steps = workflow?.steps;
  const keyParts = argKey.split('.');
  let activeKey = '';
  return keyParts.reduce((acc, curr, idx) => {
    activeKey += `${idx === 0 ? '' : '.'}${curr}`;
    const value = argumentValues?.[activeKey];
    if (value?.type === 'arrayMap') {
      const argStep = steps.find((n) => n?.id === value?.stepId);
      const newStep = { ...argStep, isArrayMap: true, arrayMapKey: activeKey };
      acc.push({ id: newStep?.id, step: newStep });
    }
    return acc;
  }, []);
};

const useStepsList = ({ search = true, allowSelfOutput = false } = {}) => {
  const apps = useSelector(selectors.appsVersions);
  const activeStep = useSelector(workflowScreenSelectors.activeWorkflowStep);
  const workflow = useSelector(workflowScreenSelectors.activeWorkflow);
  // The caret selection changes on every keystroke; only the searching variable pane needs it.
  const selection = useSelector((state) => (search ? state?.workflow?.activeInputState?.selection : null));
  const stepOutputs = useSelector((state) => state.workflow?.stepOutput);
  const activeArgKey = useSelector((state) => state?.workflow?.activeInputState?.inputArgKey);

  const selectionPrefix = String(selection?.text || '').slice(0, selection?.offset || 0);
  const searchStrings = selectionPrefix.split(' ');
  const functionInputArgumentDefs = workflow?.steps?.find((n) => n?.appSlug === 'functionInputs')?.options?.inputArguments || [];
  const workflowArgumentDefs = workflow?.meta?.arguments || [];
  const integrationMetadataDefs = workflow?.integrationMetadataDefinitions || [];
  // Offering `memory` as a variable source needs the enumeration, and a workflow that does not
  // reference memory yet is exactly the one whose author is trying to start using it.
  useWorkflowMemory();
  const hasMemory = !!workflow?.variables?.memory && Object.keys(workflow?.variables?.memory).length > 0;

  const stepId = activeStep?.id;
  const triggerSteps =
    workflow?.steps
      ?.filter((n) => n?.type === 'trigger')
      .map((n) => ({ id: n?.id, step: n }))
      .map((n) => ({ ...n, app: apps?.find((d) => d?.id === n?.step?.appId) })) || [];
  const parentSteps = getParentSteps(workflow, apps, stepId);
  const isSublistsOrSubrecords = activeArgKey?.startsWith('sublists.') || activeArgKey?.startsWith('subrecords.');
  const arrayMapArguments = isSublistsOrSubrecords ? activeStep?.options?.customArguments : activeStep?.arguments;
  const arrayMapSteps = getArrayMapSteps(workflow, arrayMapArguments, activeArgKey);
  const currentStepData = allowSelfOutput && activeStep ? [{ id: activeStep.id, step: activeStep }] : [];

  const functionInputStep = functionInputArgumentDefs?.length > 0 ? { value: 'functionInputs', name: 'Function Inputs', stepNumber: '-', icon: MdInput } : undefined;
  const workflowArgumentStep = workflowArgumentDefs?.length > 0 ? { value: 'workflowArguments', name: 'Workflow Variables', stepNumber: '-', icon: MdInput } : undefined;
  const integrationMetadataStep = integrationMetadataDefs?.length > 0 ? { value: 'integrationMetadata', name: 'Integration Metadata', stepNumber: '-', icon: MdInput } : undefined;
  const memoryStep = hasMemory ? { value: 'memory', name: 'Memory', stepNumber: '-', icon: utilityAppIcons.memory } : undefined;
  const allSteps = [...arrayMapSteps, functionInputStep, workflowArgumentStep, integrationMetadataStep, memoryStep, { value: 'system', name: 'System', stepNumber: '-', icon: utilityAppIcons.system }, ...triggerSteps, ...parentSteps, ...currentStepData].filter((n) => !!n);
  return allSteps
    .filter((n) => !!n)
    .map((n) => {
      if (['functionInputs', 'workflowArguments', 'integrationMetadata', 'memory', 'system'].includes(n.value)) {
        return n;
      }
      const step = n?.step;
      const app = apps?.find((d) => d?.id === step?.appId);
      return {
        value: step?.id || n.value,
        appId: app?.id,
        icon: app?.icon || utilityAppIcons[app?.slug] || FiStar,
        name: step?.message || n.name || 'No Description',
        description: app?.name,
        tags: [step?.stepNumber],
        stepNumber: step?.stepNumber || '-',
        isArrayMap: !!step?.isArrayMap, // Both from getArrayMapSteps method
        arrayMapKey: step?.arrayMapKey // Both from getArrayMapSteps method
      };
    })
    .filter((n) => {
      if (search === false) {
        return true;
      }
      const searchString = `${n.stepNumber}${String(n?.name).toLowerCase()?.replace(/\s+/g, '')}`;
      n.matchString = null;
      n.matchLevel = null;
      for (let i = 0; i < searchStrings.length; i++) {
        const subset = searchStrings.slice(i);
        const raw = subset.join(' ');
        const cleaned = raw.toLowerCase().replace(/\s+/g, '');
        const matchData = fuzzyMatch(searchString, cleaned);
        if (matchData.match) {
          n.matchString = raw;
          n.matchLevel = i;
          n.matchDistance = matchData.distance;
          break;
        }
      }

      return n.matchString !== null;
    })
    .map((n) => {
      const integrationMetadataDefs = workflow?.integrationMetadataDefinitions || [];
      const installationMetadata = workflow?.installationMetadata || {};
      const attributes = getAttributes({
        stepId: n.value,
        appId: n.appId,
        apps,
        workflow,
        output: stepOutputs[n.value],
        integrationMetadataDefs,
        installationMetadata
      });
      if (n.isArrayMap) {
        const argValue = arrayMapArguments?.[n.arrayMapKey];
        if (argValue?.attributeKey === 'input' || argValue?.attributeKey === 'response') {
          // This line below wasn't working as intended. It shows the custom label even when picking out the normal step, cause n.value is the same in step and arrayMap version of the step, and it picks the first one, which happens to be the arrayMap.
          /* return { ...n, name: `${n.name} > Output[]`, attributes }; */
          return { ...n, attributes };
        }
        return { ...n, name: `${n.name} > ${formatNestedAttributeKey(argValue?.attributeKey)}`, attributes: attributes.filter((n) => n.key?.startsWith(`${argValue?.attributeKey}.`)) };
      }
      return { ...n, attributes };
    })
    .filter((n) => {
      if (allowSelfOutput && activeStep?.id && n.value === activeStep?.id) {
        return true;
      }
      return ['functionInputs', 'workflowArguments', 'integrationMetadata', 'memory', 'system'].includes(n.value) || n.attributes?.length > 0;
    });
};

export default useStepsList;
